import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Lock, Award, BookOpen, Sparkles, Megaphone } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRAIN } from "@/constants/testIds";
import BrandMark from "@/components/lifted/BrandMark";
import PageMeta from "@/components/PageMeta";

const TRACK_ROUTES = {
  affiliate: "/training/affiliate",
  driver:    "/training/driver",
  partner:   "/training/partner",
  franchise: "/training/franchise",
};

export default function TrainingHub() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/training/overview").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/training/curriculum").then((r) => setCurriculum(r.data)).catch(() => setCurriculum({ modules: [], roles: [] }));
  }, []);

  if (data === null) return <LoadingShell />;
  if (data === false) return <div className="text-rose-300">Failed to load training.</div>;

  return (
    <div data-testid={TRAIN.hub} className="space-y-10">
      <PageMeta title="Training" path="/training" ogImage="/og-training.png"
                description="Lifted Academy — role-aware training for Affiliates, Drivers, Partners, and Franchisees. Certifications, modules, and the path to growth." />
      <header className="fade-up">
        <BrandMark tag="logo.training.primary" size="lg" className="mb-4" testId="training-zone-logo" />
        <div className="eyebrow">Lifted Academy</div>
        <h1 className="headline-xl">Training</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          One identity, every role's track. Complete modules, ask trainers, sit certifications.
        </p>
      </header>

      {data.announcements?.length > 0 && (
        <section className="glass-panel p-5 fade-up delay-1 border-lifted-green/20">
          <div className="flex items-center gap-2 eyebrow !text-lifted-green"><Megaphone className="h-3 w-3" /> Announcements</div>
          <ul className="mt-3 space-y-2">
            {data.announcements.slice(0, 3).map((a) => (
              <li key={a.id} className="text-sm">
                <span className="font-bebas tracking-[0.16em] text-lifted-text">{a.title}</span>
                <span className="text-lifted-dim ml-2">{a.body}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Phase 6 — 12 Universal Official Modules, role-filtered. */}
      {curriculum && curriculum.modules && curriculum.modules.length > 0 && (
        <section data-testid="training-official-modules" className="space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="eyebrow inline-flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> Official curriculum
              </div>
              <h2 className="font-anton text-2xl uppercase mt-1">
                12 modules · <span className="text-lifted-green">your view</span>
              </h2>
              {curriculum.roles?.length > 0 && (
                <p className="text-[0.65rem] text-lifted-dim mt-1 font-bebas tracking-[0.22em] uppercase">
                  Filtered for: {curriculum.roles.join(" · ")}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {curriculum.modules.map((m) => {
              const accessTone = {
                required:    "border-lifted-green/40 text-lifted-green",
                recommended: "border-sky-500/40 text-sky-300",
                overview:    "border-amber-500/40 text-amber-300",
                hidden:      "border-white/10 text-lifted-dim",
              }[m.access] || "border-white/10 text-lifted-dim";
              return (
                <Link
                  to={`/m/${m.slug}`}
                  key={m.slug}
                  className="glass-panel p-4 hover:-translate-y-0.5 transition-transform block"
                  data-testid={`training-module-tile-${m.slug}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="h-8 w-8 rounded-md bg-lifted-green/10 text-lifted-green font-bebas tracking-wider grid place-items-center shrink-0">
                      {m.order}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-lifted-text leading-tight">{m.title}</div>
                      <div className="text-[0.6rem] text-lifted-dim mt-0.5">{m.category}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[0.6rem]">
                    <span className={`font-bebas tracking-[0.18em] uppercase border rounded-full px-2 py-0.5 ${accessTone}`}>
                      {m.access}
                    </span>
                    <span className="text-lifted-dim">
                      {m.progress?.percent ?? 0}%
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.tracks.map((t, i) => {
          const unlocked = t.unlocked;
          const route = TRACK_ROUTES[t.key];
          const body = (
            <div
              data-testid={unlocked ? TRAIN.trackCard(t.key) : TRAIN.trackLocked(t.key)}
              className={`glass-panel p-6 fade-up delay-${(i % 5) + 1} transition-transform ${
                unlocked ? "hover:-translate-y-1 hover:border-lifted-green/40 cursor-pointer" : "opacity-60"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className={`h-12 w-12 rounded-xl border flex items-center justify-center ${
                  unlocked ? "border-lifted-green/40 bg-lifted-green/10 text-lifted-green" : "border-white/10 text-lifted-mute"
                }`}>
                  {t.certified ? <Award className="h-5 w-5" /> : <GraduationCap className="h-5 w-5" />}
                </div>
                {!unlocked && <span className="chip chip--locked"><Lock className="h-3 w-3" /> Locked</span>}
                {t.certified && <span className="chip chip--green">Certified</span>}
              </div>
              <div className="mt-5">
                <div className="font-anton text-2xl uppercase">{t.title}</div>
                <p className="text-sm text-lifted-dim mt-1.5">{t.description}</p>
              </div>
              <div className="mt-5 flex items-center justify-between font-bebas tracking-[0.2em] text-[0.7rem] text-lifted-mute">
                <span>{t.modules_completed} / {t.module_count} modules</span>
                <span className={unlocked ? "text-lifted-green" : ""}>{t.key.toUpperCase()}</span>
              </div>
            </div>
          );
          if (!unlocked) return <div key={t.key}>{body}</div>;
          return (
            <button
              key={t.key}
              data-testid={TRAIN.trackOpen(t.key)}
              onClick={() => navigate(route)}
              className="text-left"
            >{body}</button>
          );
        })}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Quick to="/training/modules" icon={BookOpen} label="All modules" blurb="Browse modules across your unlocked tracks." />
        <Quick to="/certification" icon={Award} label="My certifications" blurb="View earned credentials & verification codes." />
        <Quick to="/trainer" icon={Sparkles} label="Trainer dashboard" blurb="If you're a trainer / teacher — view assigned learners." gated={!user?.roles?.some(r => ["trainer","teacher","affiliate_trainer","franchisee_trainer","training_admin","admin","master_admin"].includes(r))} />
      </section>
    </div>
  );
}

function Quick({ to, icon: Icon, label, blurb, gated = false }) {
  const inner = (
    <div className={`glass-panel p-5 transition-transform ${gated ? "opacity-50" : "hover:-translate-y-1 hover:border-lifted-green/30"}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg border border-lifted-green/30 bg-lifted-green/10 text-lifted-green flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="font-anton text-xl uppercase">{label}</div>
          <div className="text-xs text-lifted-dim">{blurb}</div>
        </div>
      </div>
    </div>
  );
  return gated ? <div>{inner}</div> : <Link to={to}>{inner}</Link>;
}

function LoadingShell() {
  return <div className="font-bebas tracking-[0.3em] text-lifted-dim text-sm">LOADING TRAINING…</div>;
}
